/**
 * Map Screen - MOBILE ONLY
 * 
 * Spatial exploration of all opportunities across portfolios.
 * Clean layout: single toolbar button, filters at top, FAB at bottom.
 * Uses design tokens exclusively.
 */

import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { color, text, space, radius, icon, touch, shadow } from '../../shared/tokens/mobile';
import { ScreenContainer } from '../../components/v2/mobile';
import { 
  MapFilterChips, 
  MapPropertyCard, 
  MapView, 
  MapViewRef,
  MapToolbar,
  MapFilter,
  ParcelData,
} from '../../components/v2/map';
import { AddOpportunityModal, PortfolioSelectorModal } from '../../components/v2/opportunity';
import { useMapData, MapOpportunity } from '../../hooks/useMapData';
import { Opportunity } from '../../shared/types/opportunity';
import { authApi } from '../../shared/utils/api';
import { logger } from '../../shared/utils/logger';

export default function MapScreen() {
  const router = useRouter();
  const { focusOpportunityId, showParcels: showParcelsParam } = useLocalSearchParams<{ 
    focusOpportunityId?: string;
    showParcels?: string;
  }>();
  const mapRef = useRef<MapViewRef>(null);
  const [focusHandled, setFocusHandled] = useState(false);
  
  const {
    filteredOpportunities,
    selectedOpportunity,
    loading,
    filter,
    basemap,
    mapboxToken,
    setFilter,
    setBasemap,
    selectOpportunity,
    mapCenter,
    mapZoom,
    opportunities,
    refresh,
    setMapPosition,
  } = useMapData();

  // Handle focus on specific opportunity when coming from detail page or parcels page
  // Note: This effect runs before showParcels/handleToggleParcels are defined, so we'll handle auto-show in a separate effect
  useEffect(() => {
    if (focusOpportunityId && !focusHandled && opportunities.length > 0) {
      const targetId = parseInt(focusOpportunityId, 10);
      const targetOpp = opportunities.find(o => o.id === targetId);
      if (targetOpp && targetOpp.latitude && targetOpp.longitude) {
        // Small delay to ensure map is ready
        setTimeout(() => {
          mapRef.current?.flyTo(targetOpp.latitude, targetOpp.longitude, 16, false);
          selectOpportunity(targetOpp);
        }, 500);
        setFocusHandled(true);
      }
    }
  }, [focusOpportunityId, opportunities, focusHandled, selectOpportunity]);

  // Calculate filter counts
  const filterCounts = useMemo(() => {
    const counts: Partial<Record<MapFilter, number>> = { all: opportunities.length };
    opportunities.forEach(opp => {
      counts[opp.status] = (counts[opp.status] || 0) + 1;
    });
    return counts;
  }, [opportunities]);

  // Modal states
  const [showPortfolioSelector, setShowPortfolioSelector] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [droppedPin, setDroppedPin] = useState<{ latitude: number; longitude: number } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Parcel overlay state
  const [showParcels, setShowParcels] = useState(false);
  const [parcelLoading, setParcelLoading] = useState(false);

  const hasOpportunities = opportunities.length > 0;

  // Handlers
  const handleFilterPress = useCallback((newFilter: MapFilter) => {
    setFilter(newFilter);
  }, [setFilter]);

  const handleMarkerClick = useCallback((opp: MapOpportunity | null) => {
    selectOpportunity(opp);
    // Keep parcels visible when clicking different opportunities - only button toggles
    // Parcels will persist, and if user wants to see parcels for new opportunity, they use the button
  }, [selectOpportunity]);

  const handlePropertyPress = useCallback(() => {
    if (selectedOpportunity) {
      router.push({
        pathname: '/v2/property/[id]',
        params: { 
          id: selectedOpportunity.id.toString(), 
          portfolioId: selectedOpportunity.portfolio_id.toString() 
        },
      });
    }
  }, [router, selectedOpportunity]);

  const handleCloseCard = useCallback(() => {
    selectOpportunity(null);
    // Keep parcels visible - only button toggles them
    // Parcels stay on map even when card closes
  }, [selectOpportunity]);

  // Toggle parcel lines for selected opportunity - uses graph data for instant L1/L2/L3
  const handleToggleParcels = useCallback(async () => {
    if (!selectedOpportunity) return;
    
    if (showParcels) {
      // Hide parcels
      setShowParcels(false);
      mapRef.current?.hideParcels();
      return;
    }
    
    // Fetch and show parcels with L1/L2/L3 neighbors from graph
    setParcelLoading(true);
    try {
      const response = await authApi.getPropertyFeatures(selectedOpportunity.id, { 
        include_neighbors: true, 
        max_tier: 3 // Get L1, L2, L3 from pre-computed graph
      });
      
      if (response.success && response.features) {
        // Helper: parse numeric value safely (API may return strings)
        const parseNum = (val: any): number | null => {
          if (val == null) return null;
          const num = typeof val === 'string' ? parseFloat(val) : val;
          return isNaN(num) ? null : num;
        };
        
        // Helper to extract parcel data with proper type conversion
        const extractParcelData = (f: any): ParcelData => ({
          id: f.id || f.feature_id,
          name: f.parcel_id || f.external_id || '',
          geometry: typeof f.geometry_geojson === 'string' 
            ? JSON.parse(f.geometry_geojson) 
            : f.geometry_geojson,
          acreage: parseNum(f.area_acres) ?? parseNum(f.acreage),
          taxValue: parseNum(f.total_value),
          askingPrice: null,
        });
        
        // Primary parcel - filter by layer_code and association_type
        const primaryFeature = response.features.find(
          (f: any) => f.layer_code === 'parcel' && f.association_type === 'primary' && f.geometry_geojson
        );
        const primaryParcels: ParcelData[] = primaryFeature 
          ? [extractParcelData(primaryFeature)] 
          : [];
        
        // Extract neighbors from neighborsByTier (graph data) - INSTANT, no API calls!
        const neighborsByTier: Record<number, ParcelData[]> = {};
        
        if (response.neighborsByTier && response.neighborsByTier.length > 0) {
          // Use graph data (instant) - neighborsByTier has L1/L2/L3 with full geometry
          response.neighborsByTier.forEach((tier: any) => {
            neighborsByTier[tier.tier] = tier.neighbors
              .filter((n: any) => n.geometry_geojson)
              .map((n: any) => {
                const neighbor = n as any; // Graph returns flat structure
                return {
                  id: neighbor.id,
                  name: neighbor.parcel_id || '',
                  geometry: typeof neighbor.geometry_geojson === 'string'
                    ? JSON.parse(neighbor.geometry_geojson)
                    : neighbor.geometry_geojson,
                  acreage: parseNum(neighbor.area_acres),
                  taxValue: parseNum(neighbor.total_value),
                  askingPrice: null,
                };
              });
          });
          
          logger.info('Using graph data for neighbors', 'map', {
            source: response.neighborSource,
            tiers: Object.keys(neighborsByTier).map(Number),
            totalNeighbors: Object.values(neighborsByTier).reduce((sum, arr) => sum + arr.length, 0),
          });
        } else {
          // Fallback: extract from features array (API cache)
          logger.warn('No neighborsByTier - falling back to features array', 'map');
          response.features
            .filter((f: any) => 
              f.layer_code === 'parcel' &&
              f.association_type !== 'primary' && 
              f.geometry_geojson && 
              f.neighbor_tier
            )
            .forEach((f: any) => {
              const tier = f.neighbor_tier || 1;
              if (!neighborsByTier[tier]) neighborsByTier[tier] = [];
              neighborsByTier[tier].push(extractParcelData(f));
            });
        }
        
        // Pass tiered neighbors to map
        mapRef.current?.showParcels(primaryParcels, neighborsByTier);
        setShowParcels(true);
        
        logger.debug('Showing parcel lines', 'map', { 
          primary: primaryParcels.length, 
          neighborsByTier: Object.keys(neighborsByTier).map(Number),
          totalNeighbors: Object.values(neighborsByTier).reduce((sum, arr) => sum + arr.length, 0),
          source: response.neighborSource || 'api_cache',
        });
      } else {
        logger.warn('No parcel data available', 'map');
      }
    } catch (err) {
      logger.error('Failed to fetch parcel data', err, 'map');
    } finally {
      setParcelLoading(false);
    }
  }, [selectedOpportunity, showParcels]);

  // Auto-show parcels when navigating from parcels page (after handleToggleParcels is defined)
  useEffect(() => {
    if (showParcelsParam === 'true' && selectedOpportunity && !showParcels && !parcelLoading) {
      // Wait a bit for opportunity to be fully selected and map ready
      const timer = setTimeout(() => {
        handleToggleParcels();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [showParcelsParam, selectedOpportunity, showParcels, parcelLoading, handleToggleParcels]);

  const handleAddOpportunity = useCallback(() => {
    logger.debug('Add opportunity from FAB', 'map');
    setDroppedPin(null);
    setShowPortfolioSelector(true);
  }, []);

  // Long press - save coords and show pin in iframe
  const handleLongPress = useCallback((coords: { latitude: number; longitude: number }) => {
    logger.debug('Map long press - pin dropped', 'map', coords);
    setDroppedPin(coords);
    mapRef.current?.showDroppedPin(coords.latitude, coords.longitude);
  }, []);

  // User clicked on the dropped pin - now show portfolio selector
  const handleDroppedPinClick = useCallback((coords: { latitude: number; longitude: number }) => {
    logger.debug('Dropped pin clicked - showing portfolio selector', 'map', coords);
    setDroppedPin(coords);
    setShowPortfolioSelector(true);
  }, []);

  // User cancelled the dropped pin (clicked elsewhere)
  const handleDroppedPinCancel = useCallback(() => {
    logger.debug('Dropped pin cancelled', 'map');
    setDroppedPin(null);
  }, []);

  const handlePortfolioSelect = useCallback((portfolioId: number) => {
    logger.debug('Portfolio selected for add opportunity', 'map', { portfolioId });
    setSelectedPortfolioId(portfolioId);
    setShowPortfolioSelector(false);
    setTimeout(() => {
      setShowAddModal(true);
    }, 100);
  }, []);

  const handleAddSuccess = useCallback((opportunity: Opportunity) => {
    logger.info('Opportunity created from map', 'map', { id: opportunity.id });
    setShowAddModal(false);
    setDroppedPin(null);
    // Remove dropped pin from map
    mapRef.current?.removeDroppedPin();
    refresh();
    router.push({
      pathname: '/v2/property/[id]',
      params: { 
        id: opportunity.id.toString(), 
        portfolioId: selectedPortfolioId?.toString() 
      },
    });
  }, [router, selectedPortfolioId, refresh]);

  // Toolbar handlers
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
  }, [refresh]);

  const handleFitAll = useCallback(() => {
    mapRef.current?.fitAllMarkers();
  }, []);

  const handleFlyTo = useCallback((lat: number, lng: number, zoomLevel?: number, showLocation?: boolean) => {
    mapRef.current?.flyTo(lat, lng, zoomLevel, showLocation);
  }, []);

  return (
    <ScreenContainer>
      <View style={styles.container}>
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>Map</Text>
          {hasOpportunities && (
            <View style={styles.headerRight}>
              <Text style={styles.count}>
                {filteredOpportunities.length} {filteredOpportunities.length === 1 ? 'pin' : 'pins'}
              </Text>
            </View>
          )}
        </View>

        {/* FILTER CHIPS - in header area, not floating */}
        {hasOpportunities && (
          <MapFilterChips
            activeFilter={filter}
            onFilterPress={handleFilterPress}
            counts={filterCounts}
          />
        )}

        {/* MAP AREA */}
        <View style={styles.mapContainer}>
          <MapView
            ref={mapRef}
            opportunities={filteredOpportunities}
            selectedOpportunity={selectedOpportunity}
            mapboxToken={mapboxToken}
            center={mapCenter}
            zoom={mapZoom}
            basemap={basemap}
            onMarkerClick={handleMarkerClick}
            onLongPress={handleLongPress}
            onDroppedPinClick={handleDroppedPinClick}
            onDroppedPinCancel={handleDroppedPinCancel}
            onPositionChange={setMapPosition}
            loading={loading}
          />

          {/* UNIFIED TOOLBAR - single button top left */}
          <MapToolbar
            mapboxToken={mapboxToken}
            currentBasemap={basemap}
            onBasemapChange={setBasemap}
            onRefresh={handleRefresh}
            onFitAll={handleFitAll}
            onFlyTo={handleFlyTo}
            hasOpportunities={hasOpportunities}
            refreshing={refreshing}
          />

          {/* ADD OPPORTUNITY FAB */}
          <Pressable
            onPress={handleAddOpportunity}
            style={({ pressed }) => [styles.fab, pressed && styles.fabPressed]}
            accessibilityLabel="Add opportunity"
            accessibilityRole="button"
          >
            <Ionicons name="add" size={icon.lg} color={color.textInverse} />
          </Pressable>

          {/* SELECTED PROPERTY CARD */}
          {selectedOpportunity && (
            <MapPropertyCard
              name={selectedOpportunity.name}
              address={selectedOpportunity.address}
              askingPrice={selectedOpportunity.asking_price}
              taxValue={selectedOpportunity.property_value}
              acres={selectedOpportunity.acreage}
              status={selectedOpportunity.status}
              onPress={handlePropertyPress}
              onClose={handleCloseCard}
              showParcels={showParcels}
              onToggleParcels={handleToggleParcels}
              parcelLoading={parcelLoading}
              county={selectedOpportunity.county}
              owner={selectedOpportunity.owner_name}
              parcelId={selectedOpportunity.parcel_id}
            />
          )}
        </View>

        {/* PORTFOLIO SELECTOR MODAL */}
        <PortfolioSelectorModal
          visible={showPortfolioSelector}
          onClose={() => {
            setShowPortfolioSelector(false);
            setDroppedPin(null);
            mapRef.current?.removeDroppedPin();
          }}
          onSelect={handlePortfolioSelect}
        />

        {/* ADD OPPORTUNITY MODAL */}
        {selectedPortfolioId && (
          <AddOpportunityModal
            visible={showAddModal}
            portfolioId={selectedPortfolioId}
            onClose={() => {
              setShowAddModal(false);
              setDroppedPin(null);
              mapRef.current?.removeDroppedPin();
            }}
            onSuccess={handleAddSuccess}
            initialCoordinates={droppedPin || undefined}
            initialMode={droppedPin ? 'coordinates' : 'url'}
          />
        )}
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: space.md,
    paddingTop: space.md,
    paddingBottom: space.sm,
  },
  title: {
    fontSize: text.pageTitle.fontSize,
    fontWeight: text.pageTitle.fontWeight,
    lineHeight: text.pageTitle.lineHeight,
    letterSpacing: text.pageTitle.letterSpacing,
    color: color.textPrimary,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  count: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
  },
  mapContainer: {
    flex: 1,
    backgroundColor: color.backgroundSecondary,
    position: 'relative',
  },
  fab: {
    position: 'absolute',
    bottom: space.lg,
    right: space.md,
    width: touch.recommended,
    height: touch.recommended,
    borderRadius: radius.full,
    backgroundColor: color.primary,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    ...shadow.sm,
  },
  fabPressed: {
    backgroundColor: color.primaryDark,
  },
});
